import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  BarChart, Bar, LineChart, Line, AreaChart, Area, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ComposedChart
} from 'recharts';
import { 
  TrendingUp, TrendingDown, Users, Target, DollarSign, Send,
  CheckCircle2, XCircle, Clock, RefreshCw, Download, Filter,
  Calendar, ChevronRight, ChevronDown, BarChart3, PieChart as PieChartIcon,
  Activity, Zap, MessageSquare, ArrowUpRight, ArrowDownRight,
  Sparkles, AlertTriangle, Eye, RotateCcw, Loader2, FileText
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

import { API_BASE_URL as API } from '../utils/apiConfig';

// Colors
const COLORS = ['#8B5CF6', '#EC4899', '#10B981', '#F59E0B', '#3B82F6', '#EF4444', '#06B6D4', '#84CC16'];
const STATUS_COLORS = {
  completed: '#10B981',
  executing: '#3B82F6',
  approved: '#8B5CF6',
  pending_approval: '#F59E0B',
  draft: '#6B7280',
  failed: '#EF4444',
  paused: '#F97316'
};

// Format helpers
const formatNumber = (num) => {
  if (num === null || num === undefined) return '—';
  if (Math.abs(num) >= 10000000) return `${(num / 10000000).toFixed(2)} Cr`;
  if (Math.abs(num) >= 100000) return `${(num / 100000).toFixed(2)} L`;
  if (Math.abs(num) >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toLocaleString('en-IN');
};

const formatCurrency = (num) => `₹${formatNumber(num)}`;
const formatPercent = (num) => `${(num || 0).toFixed(1)}%`;

// KPI Card Component
const KPICard = ({ title, value, change, changeType, icon: Icon, trend, subtitle, color = 'violet' }) => {
  const isPositive = changeType === 'positive' || (change > 0 && changeType !== 'negative');
  const TrendIcon = isPositive ? ArrowUpRight : ArrowDownRight;
  
  return (
    <Card className="relative overflow-hidden">
      <div className={`absolute inset-0 bg-gradient-to-br from-${color}-500/10 to-transparent`} />
      <CardContent className="p-4 relative">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">{title}</p>
            <p className="text-2xl font-bold">{value}</p>
            {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
          </div>
          <div className={`p-2 rounded-lg bg-${color}-500/20`}>
            <Icon className={`w-5 h-5 text-${color}-500`} />
          </div>
        </div>
        {change !== undefined && (
          <div className={`flex items-center gap-1 mt-2 text-xs ${isPositive ? 'text-green-500' : 'text-red-500'}`}>
            <TrendIcon className="w-3 h-3" />
            <span>{Math.abs(change).toFixed(1)}% {trend || 'vs last period'}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Campaign Row Component for Drill-Down
const CampaignRow = ({ campaign, onSelect, isSelected }) => {
  const statusColor = STATUS_COLORS[campaign.status] || '#6B7280';
  const deliveryRate = campaign.analytics?.delivery_rate || 0;
  const responseRate = campaign.analytics?.response_rate || 0;
  const roi = campaign.analytics?.roi || 0;
  
  return (
    <div 
      className={`p-4 border-b last:border-b-0 cursor-pointer hover:bg-muted/50 transition-colors ${isSelected ? 'bg-violet-500/10 border-l-4 border-l-violet-500' : ''}`}
      onClick={() => onSelect(campaign)}
      data-testid={`campaign-row-${campaign.campaign_id}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: statusColor }} />
          <div>
            <p className="font-medium">{campaign.name || campaign.campaign_name}</p>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span className="capitalize">{campaign.type || 'cohort'}</span>
              <span>•</span>
              <span>{new Date(campaign.created_at || campaign.executed_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' })}</span>
              {campaign.is_retarget && (
                <>
                  <span>•</span>
                  <Badge variant="outline" className="text-xs py-0">Retarget</Badge>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-6">
          <div className="text-right">
            <p className="text-sm font-medium">{formatNumber(campaign.analytics?.total_audience || 0)}</p>
            <p className="text-xs text-muted-foreground">Audience</p>
          </div>
          <div className="text-right">
            <p className="text-sm font-medium text-green-500">{formatPercent(deliveryRate)}</p>
            <p className="text-xs text-muted-foreground">Delivered</p>
          </div>
          <div className="text-right">
            <p className="text-sm font-medium text-blue-500">{formatPercent(responseRate)}</p>
            <p className="text-xs text-muted-foreground">Response</p>
          </div>
          <div className="text-right">
            <p className={`text-sm font-medium ${roi >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
              {roi >= 0 ? '+' : ''}{formatPercent(roi)}
            </p>
            <p className="text-xs text-muted-foreground">ROI</p>
          </div>
          <ChevronRight className="w-4 h-4 text-muted-foreground" />
        </div>
      </div>
    </div>
  );
};

// Campaign Detail Panel
const CampaignDetailPanel = ({ campaign, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [deliveryData, setDeliveryData] = useState(null);
  
  useEffect(() => {
    if (campaign) {
      fetchDeliveryData();
    }
  }, [campaign]);
  
  const fetchDeliveryData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/api/campaigns/${campaign.campaign_id}/delivery-report`,
        { withCredentials: true }
      );
      setDeliveryData(response.data);
    } catch (err) {
      console.error('Fetch delivery data error:', err);
    } finally {
      setLoading(false);
    }
  };
  
  if (!campaign) return null;
  
  const analytics = campaign.analytics || {};
  const summary = deliveryData?.summary || {};
  const disposition = deliveryData?.disposition_breakdown || {};
  
  // Prepare disposition pie chart data
  const dispositionData = Object.entries(disposition).map(([key, data]) => ({
    name: key.charAt(0).toUpperCase() + key.slice(1),
    value: data.count || 0,
    percentage: data.percentage || 0
  })).filter(d => d.value > 0);
  
  // Timeline data (mock for now - would come from actual delivery timeline)
  const timelineData = [
    { hour: '10:00', sent: 100, delivered: 95, failed: 5 },
    { hour: '10:15', sent: 500, delivered: 460, failed: 40 },
    { hour: '10:30', sent: 1200, delivered: 1100, failed: 100 },
    { hour: '10:45', sent: 800, delivered: 740, failed: 60 },
    { hour: '11:00', sent: 400, delivered: 370, failed: 30 }
  ];
  
  return (
    <Card className="h-full">
      <CardHeader className="border-b">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg">{campaign.name || campaign.campaign_name}</CardTitle>
            <p className="text-sm text-muted-foreground capitalize">{campaign.type || 'cohort'} Campaign</p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <XCircle className="w-4 h-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-4 space-y-6 overflow-y-auto max-h-[calc(100vh-300px)]">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin" />
          </div>
        ) : (
          <>
            {/* Summary Stats */}
            <div className="grid grid-cols-4 gap-3">
              <div className="p-3 rounded-lg bg-muted/50 text-center">
                <p className="text-lg font-bold">{formatNumber(summary.total_audience || analytics.total_audience || 0)}</p>
                <p className="text-xs text-muted-foreground">Total Sent</p>
              </div>
              <div className="p-3 rounded-lg bg-green-500/10 text-center">
                <p className="text-lg font-bold text-green-500">{formatNumber(summary.delivered || analytics.delivered || 0)}</p>
                <p className="text-xs text-muted-foreground">Delivered</p>
              </div>
              <div className="p-3 rounded-lg bg-red-500/10 text-center">
                <p className="text-lg font-bold text-red-500">{formatNumber(summary.failed || analytics.failed || 0)}</p>
                <p className="text-xs text-muted-foreground">Failed</p>
              </div>
              <div className="p-3 rounded-lg bg-blue-500/10 text-center">
                <p className="text-lg font-bold text-blue-500">{formatPercent(summary.delivery_rate || analytics.delivery_rate || 0)}</p>
                <p className="text-xs text-muted-foreground">Delivery Rate</p>
              </div>
            </div>
            
            {/* Disposition Breakdown */}
            {dispositionData.length > 0 && (
              <div className="space-y-3">
                <h4 className="font-semibold text-sm">Disposition Breakdown</h4>
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={dispositionData}
                        cx="50%"
                        cy="50%"
                        innerRadius={40}
                        outerRadius={70}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        {dispositionData.map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={entry.name === 'Delivered' ? '#10B981' : entry.name === 'Failed' ? '#EF4444' : COLORS[index % COLORS.length]} 
                          />
                        ))}
                      </Pie>
                      <Tooltip 
                        formatter={(value, name) => [formatNumber(value), name]}
                        contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }}
                      />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
            
            {/* Delivery Timeline */}
            <div className="space-y-3">
              <h4 className="font-semibold text-sm">Delivery Timeline</h4>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={timelineData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="hour" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }}
                    />
                    <Area type="monotone" dataKey="delivered" stackId="1" fill="#10B981" stroke="#10B981" fillOpacity={0.6} name="Delivered" />
                    <Area type="monotone" dataKey="failed" stackId="1" fill="#EF4444" stroke="#EF4444" fillOpacity={0.6} name="Failed" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
            
            {/* ROI & Cost */}
            <div className="space-y-3">
              <h4 className="font-semibold text-sm">Campaign Economics</h4>
              <div className="grid grid-cols-2 gap-3">
                <Card className="bg-gradient-to-br from-emerald-500/10 to-green-500/5">
                  <CardContent className="p-4 text-center">
                    <DollarSign className="w-6 h-6 mx-auto mb-2 text-emerald-500" />
                    <p className="text-xl font-bold text-emerald-500">
                      {formatCurrency(analytics.revenue_attributed || 0)}
                    </p>
                    <p className="text-xs text-muted-foreground">Revenue Generated</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-red-500/10 to-orange-500/5">
                  <CardContent className="p-4 text-center">
                    <MessageSquare className="w-6 h-6 mx-auto mb-2 text-red-500" />
                    <p className="text-xl font-bold text-red-500">
                      {formatCurrency(campaign.cost_estimate?.total_cost || analytics.cost || 0)}
                    </p>
                    <p className="text-xs text-muted-foreground">Campaign Cost</p>
                  </CardContent>
                </Card>
              </div>
            </div>
            
            {/* Retarget Potential */}
            {deliveryData?.retarget_potential && campaign.status === 'completed' && !campaign.is_retarget && (
              <Card className="bg-gradient-to-r from-violet-500/10 to-purple-500/5 border-violet-500/30">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <RotateCcw className="w-5 h-5 text-violet-500" />
                      <div>
                        <p className="font-semibold">Retarget Opportunity</p>
                        <p className="text-sm text-muted-foreground">
                          {formatNumber(deliveryData.retarget_potential.eligible_count || 0)} non-responders available
                        </p>
                      </div>
                    </div>
                    <Button size="sm" className="gap-2">
                      <RotateCcw className="w-3 h-3" />
                      Create Retarget
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Actions */}
            <div className="flex gap-2 pt-4 border-t">
              <Button variant="outline" size="sm" className="gap-2 flex-1">
                <Download className="w-4 h-4" />
                Export Report
              </Button>
              <Button variant="outline" size="sm" className="gap-2 flex-1">
                <FileText className="w-4 h-4" />
                Download PDF
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

// Main Analytics Dashboard Component
export default function CampaignAnalyticsDashboard({ brandId, onBack }) {
  const [loading, setLoading] = useState(true);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [timeRange, setTimeRange] = useState('30d');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('overview');
  
  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch campaigns and aggregate analytics
      const [campaignsRes, analyticsRes] = await Promise.all([
        axios.get(`${API}/api/campaigns?brand_id=${brandId}`, { withCredentials: true }),
        axios.get(`${API}/api/campaigns/analytics-summary?brand_id=${brandId}&time_range=${timeRange}`, { withCredentials: true })
          .catch(() => ({ data: null }))
      ]);
      
      const campaignsList = campaignsRes.data.data?.campaigns || [];
      setCampaigns(campaignsList);
      
      // Calculate aggregate metrics from campaigns
      const completedCampaigns = campaignsList.filter(c => c.status === 'completed');
      const totalAudience = completedCampaigns.reduce((sum, c) => sum + (c.analytics?.total_audience || 0), 0);
      const totalDelivered = completedCampaigns.reduce((sum, c) => sum + (c.analytics?.delivered || 0), 0);
      const totalFailed = completedCampaigns.reduce((sum, c) => sum + (c.analytics?.failed || 0), 0);
      const totalRevenue = completedCampaigns.reduce((sum, c) => sum + (c.analytics?.revenue_attributed || 0), 0);
      const totalCost = completedCampaigns.reduce((sum, c) => sum + (c.cost_estimate?.total_cost || 0), 0);
      
      const avgDeliveryRate = totalAudience > 0 ? (totalDelivered / totalAudience * 100) : 0;
      const avgResponseRate = completedCampaigns.length > 0 
        ? completedCampaigns.reduce((sum, c) => sum + (c.analytics?.response_rate || 0), 0) / completedCampaigns.length
        : 0;
      const roi = totalCost > 0 ? ((totalRevenue - totalCost) / totalCost * 100) : 0;
      
      // Status distribution
      const statusCounts = campaignsList.reduce((acc, c) => {
        acc[c.status] = (acc[c.status] || 0) + 1;
        return acc;
      }, {});
      
      // Monthly trend (mock calculation based on campaigns)
      const monthlyTrend = generateMonthlyTrend(campaignsList);
      
      setAnalyticsData({
        summary: {
          total_campaigns: campaignsList.length,
          completed_campaigns: completedCampaigns.length,
          total_audience: totalAudience,
          total_delivered: totalDelivered,
          total_failed: totalFailed,
          avg_delivery_rate: avgDeliveryRate,
          avg_response_rate: avgResponseRate,
          total_revenue: totalRevenue,
          total_cost: totalCost,
          roi: roi
        },
        status_distribution: Object.entries(statusCounts).map(([status, count]) => ({
          name: status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          value: count,
          status
        })),
        monthly_trend: monthlyTrend,
        top_performers: completedCampaigns
          .filter(c => c.analytics?.roi)
          .sort((a, b) => (b.analytics?.roi || 0) - (a.analytics?.roi || 0))
          .slice(0, 5)
      });
      
    } catch (err) {
      console.error('Fetch analytics error:', err);
      toast.error('Failed to load campaign analytics');
    } finally {
      setLoading(false);
    }
  }, [brandId, timeRange]);
  
  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);
  
  const generateMonthlyTrend = (campaigns) => {
    // Group campaigns by month
    const months = {};
    const now = new Date();
    
    // Initialize last 6 months
    for (let i = 5; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
      months[key] = { month: key, campaigns: 0, audience: 0, delivered: 0, revenue: 0 };
    }
    
    // Populate with campaign data
    campaigns.forEach(c => {
      const date = new Date(c.created_at || c.executed_at);
      const key = date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
      if (months[key]) {
        months[key].campaigns++;
        months[key].audience += c.analytics?.total_audience || 0;
        months[key].delivered += c.analytics?.delivered || 0;
        months[key].revenue += c.analytics?.revenue_attributed || 0;
      }
    });
    
    return Object.values(months);
  };
  
  const filteredCampaigns = campaigns.filter(c => 
    statusFilter === 'all' || c.status === statusFilter
  );
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }
  
  const summary = analyticsData?.summary || {};
  
  return (
    <div className="space-y-6" data-testid="campaign-analytics-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-violet-500" />
            Campaign Analytics
          </h1>
          <p className="text-muted-foreground">Comprehensive view of all campaign performance</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex border rounded-lg overflow-hidden">
            {['7d', '30d', '90d', 'all'].map(range => (
              <Button
                key={range}
                variant={timeRange === range ? 'default' : 'ghost'}
                size="sm"
                className="rounded-none"
                onClick={() => setTimeRange(range)}
              >
                {range === 'all' ? 'All Time' : range.toUpperCase()}
              </Button>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={fetchAnalytics}>
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" className="gap-2">
            <Download className="w-4 h-4" />
            Export
          </Button>
        </div>
      </div>
      
      {/* KPI Cards */}
      <div className="grid md:grid-cols-5 gap-4">
        <KPICard
          title="Total Campaigns"
          value={summary.total_campaigns || 0}
          subtitle={`${summary.completed_campaigns || 0} completed`}
          icon={Zap}
          color="violet"
        />
        <KPICard
          title="Total Audience Reached"
          value={formatNumber(summary.total_audience || 0)}
          change={12.5}
          changeType="positive"
          icon={Users}
          color="blue"
        />
        <KPICard
          title="Avg. Delivery Rate"
          value={formatPercent(summary.avg_delivery_rate)}
          change={2.3}
          changeType="positive"
          icon={CheckCircle2}
          color="emerald"
        />
        <KPICard
          title="Avg. Response Rate"
          value={formatPercent(summary.avg_response_rate)}
          change={-1.2}
          changeType="negative"
          icon={Activity}
          color="pink"
        />
        <KPICard
          title="Portfolio ROI"
          value={formatPercent(summary.roi)}
          change={summary.roi}
          changeType={summary.roi >= 0 ? 'positive' : 'negative'}
          icon={TrendingUp}
          color="amber"
        />
      </div>
      
      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview" className="gap-2">
            <BarChart3 className="w-4 h-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="campaigns" className="gap-2">
            <MessageSquare className="w-4 h-4" />
            All Campaigns ({campaigns.length})
          </TabsTrigger>
          <TabsTrigger value="performance" className="gap-2">
            <TrendingUp className="w-4 h-4" />
            Performance
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="overview" className="space-y-6 mt-6">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Status Distribution */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <PieChartIcon className="w-4 h-4" />
                  Campaign Status Distribution
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={analyticsData?.status_distribution || []}
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        paddingAngle={2}
                        dataKey="value"
                        label={({ name, value }) => `${name}: ${value}`}
                      >
                        {(analyticsData?.status_distribution || []).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={STATUS_COLORS[entry.status] || COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
            
            {/* Monthly Trend */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Activity className="w-4 h-4" />
                  Monthly Campaign Activity
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={analyticsData?.monthly_trend || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                      <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
                      <Tooltip 
                        formatter={(value, name) => [formatNumber(value), name]}
                        contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }}
                      />
                      <Legend />
                      <Bar yAxisId="left" dataKey="campaigns" fill="#8B5CF6" name="Campaigns" radius={[4, 4, 0, 0]} />
                      <Line yAxisId="right" type="monotone" dataKey="delivered" stroke="#10B981" strokeWidth={2} name="Delivered" dot={{ r: 4 }} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Revenue vs Cost */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <DollarSign className="w-4 h-4" />
                Revenue vs Cost Analysis
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={analyticsData?.monthly_trend || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={(v) => formatNumber(v)} tick={{ fontSize: 11 }} />
                    <Tooltip 
                      formatter={(value, name) => [formatCurrency(value), name]}
                      contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }}
                    />
                    <Legend />
                    <Bar dataKey="revenue" fill="#10B981" name="Revenue" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
          
          {/* Top Performers */}
          {analyticsData?.top_performers?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-amber-500" />
                  Top Performing Campaigns
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {analyticsData.top_performers.map((campaign, idx) => (
                    <div key={campaign.campaign_id || idx} className="flex items-center justify-between p-3 rounded-lg bg-muted/30 hover:bg-muted/50 cursor-pointer">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center text-white font-bold text-sm">
                          #{idx + 1}
                        </div>
                        <div>
                          <p className="font-medium">{campaign.name || campaign.campaign_name}</p>
                          <p className="text-xs text-muted-foreground capitalize">{campaign.type} • {formatNumber(campaign.analytics?.total_audience || 0)} audience</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-emerald-500">+{formatPercent(campaign.analytics?.roi || 0)} ROI</p>
                        <p className="text-xs text-muted-foreground">{formatCurrency(campaign.analytics?.revenue_attributed || 0)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
        
        <TabsContent value="campaigns" className="mt-6">
          <div className="grid md:grid-cols-5 gap-6">
            {/* Campaign List */}
            <div className="md:col-span-3">
              <Card>
                <CardHeader className="border-b">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">All Campaigns</CardTitle>
                    <div className="flex items-center gap-2">
                      <select 
                        className="text-sm border rounded-md px-2 py-1 bg-background"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                      >
                        <option value="all">All Status</option>
                        <option value="completed">Completed</option>
                        <option value="executing">Executing</option>
                        <option value="approved">Approved</option>
                        <option value="pending_approval">Pending Approval</option>
                        <option value="draft">Draft</option>
                      </select>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-0 max-h-[600px] overflow-y-auto">
                  {filteredCampaigns.length === 0 ? (
                    <div className="p-8 text-center text-muted-foreground">
                      No campaigns found
                    </div>
                  ) : (
                    filteredCampaigns.map(campaign => (
                      <CampaignRow
                        key={campaign.campaign_id || campaign._id}
                        campaign={campaign}
                        onSelect={setSelectedCampaign}
                        isSelected={selectedCampaign?.campaign_id === campaign.campaign_id}
                      />
                    ))
                  )}
                </CardContent>
              </Card>
            </div>
            
            {/* Detail Panel */}
            <div className="md:col-span-2">
              {selectedCampaign ? (
                <CampaignDetailPanel
                  campaign={selectedCampaign}
                  onClose={() => setSelectedCampaign(null)}
                />
              ) : (
                <Card className="h-full flex items-center justify-center">
                  <CardContent className="text-center py-12">
                    <Eye className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                    <p className="text-muted-foreground">Select a campaign to view details</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </TabsContent>
        
        <TabsContent value="performance" className="mt-6 space-y-6">
          {/* Delivery Rate Trend */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <TrendingUp className="w-4 h-4" />
                Delivery Performance Trend
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={analyticsData?.monthly_trend || []}>
                    <defs>
                      <linearGradient id="deliveredGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={(v) => formatNumber(v)} tick={{ fontSize: 11 }} />
                    <Tooltip 
                      formatter={(value, name) => [formatNumber(value), name]}
                      contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }}
                    />
                    <Legend />
                    <Area 
                      type="monotone" 
                      dataKey="audience" 
                      stroke="#8B5CF6" 
                      fill="url(#deliveredGradient)"
                      fillOpacity={0.3}
                      name="Total Audience"
                    />
                    <Area 
                      type="monotone" 
                      dataKey="delivered" 
                      stroke="#10B981" 
                      fill="url(#deliveredGradient)"
                      name="Delivered"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
          
          {/* Performance Summary Cards */}
          <div className="grid md:grid-cols-3 gap-4">
            <Card className="bg-gradient-to-br from-green-500/10 to-emerald-500/5">
              <CardContent className="p-6 text-center">
                <CheckCircle2 className="w-10 h-10 mx-auto mb-3 text-green-500" />
                <p className="text-3xl font-bold text-green-500">{formatNumber(summary.total_delivered || 0)}</p>
                <p className="text-sm text-muted-foreground">Total Messages Delivered</p>
                <p className="text-xs text-green-600 mt-2">
                  {formatPercent(summary.avg_delivery_rate)} success rate
                </p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-red-500/10 to-orange-500/5">
              <CardContent className="p-6 text-center">
                <XCircle className="w-10 h-10 mx-auto mb-3 text-red-500" />
                <p className="text-3xl font-bold text-red-500">{formatNumber(summary.total_failed || 0)}</p>
                <p className="text-sm text-muted-foreground">Total Failed Deliveries</p>
                <p className="text-xs text-red-600 mt-2">
                  {formatPercent(summary.total_audience > 0 ? (summary.total_failed / summary.total_audience * 100) : 0)} failure rate
                </p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-blue-500/10 to-cyan-500/5">
              <CardContent className="p-6 text-center">
                <DollarSign className="w-10 h-10 mx-auto mb-3 text-blue-500" />
                <p className="text-3xl font-bold text-blue-500">{formatCurrency(summary.total_revenue || 0)}</p>
                <p className="text-sm text-muted-foreground">Total Revenue Generated</p>
                <p className="text-xs text-blue-600 mt-2">
                  {formatPercent(summary.roi)} ROI
                </p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
